import axios from "axios";
import { getBaseURL } from "../generic/config";
import { getAccessToken, getSelectedInstanceId } from "../generic/authStore";
import useToast from "@/base-components/hooks/useToast";

const getDefaultHeaders = (excludeBearerToken = false, excludeContentType = false) => {
    const headers: Record<string, string> = {};

    if (!excludeContentType) {
        headers["Content-Type"] = "application/json";
    }

    if (!excludeBearerToken) {
        const token = getAccessToken();
        if (token) {
            headers["Authorization"] = "Bearer " + token;
        }
        // The selected tenant; the backend validates membership and applies RLS.
        const instanceId = getSelectedInstanceId();
        if (instanceId != null) {
            headers["X-Instance-Id"] = String(instanceId);
        }
    }

    return headers;
}

export const getAxiosInstance = (options: { excludeBearerToken?: boolean, skipErrorInterceptor?: boolean, excludeContentType?: boolean } = { skipErrorInterceptor: false }) => {
    const instance = axios.create({
        baseURL: getBaseURL(),
        headers: getDefaultHeaders(options.excludeBearerToken, options.excludeContentType),
    });

    if (!options.skipErrorInterceptor) {
        instance.interceptors.response.use(
            response => response,
            async error => {
                if (error.response && (error.response.status !== 200 && error.response.status !== 201)) {
                    console.error("Non-OK response from server:", error.response.status);
                    const customError = error.response.data;
                    const customDescription = typeof customError === "string" ? customError : (customError && "message" in customError ? customError.message : (customError && "error" in customError ? customError.error : null));

                    if (customDescription) {
                        useToast()("Error occurred", customDescription, "error");
                    } else if (error.response.status === 401) {
                        useToast()("Unauthorized", "Your session has expired or you are not signed in.", "error");
                    } else if (error.response.status === 403) {
                        useToast()("Forbidden", "You do not have access to this instance or action.", "error");
                    } else {
                        useToast()("Error occurred", "An unexpected error occurred. Please try again later.", "error");
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    return instance;
};
